import {
    CdkDrag,
    CdkDragDrop,
    CdkDragHandle,
    CdkDropList,
    moveItemInArray,
} from '@angular/cdk/drag-drop';
import {
    Component,
    computed,
    DestroyRef,
    input,
    output,
    signal,
    WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FaqListModel } from 'lemunzo-api';
import { debounceTime, distinctUntilChanged, merge, startWith } from 'rxjs';
import { HorizontalPosition } from '../../enums/horizontal-position';
import { stripHtmlTags } from '../../formValidators/whitespace-or-empty-html-validator';
import { LanguageCode } from '../../models/language-code';
import { ConstantsService } from '../../services/constants.service';
import { LocalizationService } from '../../services/localization.service';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';
import { TooltipCustomComponent } from '../tooltip-custom/tooltip-custom.component';
import { TranslationComponent } from '../translation/translation.component';

@Component({
    selector: 'apcam-faq-list',
    imports: [
        CdkDrag,
        CdkDragHandle,
        CdkDropList,
        LoadingSpinnerComponent,
        MatButtonModule,
        MatChipsModule,
        MatExpansionModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatTooltipModule,
        ReactiveFormsModule,
        TooltipCustomComponent,
        TranslatePipe,
        TranslationComponent,
    ],
    templateUrl: './faq-list.component.html',
    styleUrl: './faq-list.component.scss',
})
export class FaqListComponent {
    /** List of FAQs to display; filter is recomputed when this reference changes. */
    readonly faqs = input.required<FaqListModel[]>();

    /** Whether the user is an admin (action buttons, translation warnings). */
    readonly isAdmin = input.required<boolean>();

    /**
     * Optional custom key from translations' file when a FAQ translation is missing.
     */
    readonly missingTranslationKey = input<string>(
        'shared.faq.translationNotAvailable'
    );

    /**
     * Key: FAQ ID; value: whether an action is in progress on that FAQ.
     */
    readonly performingActionFaqIds = input<Map<number, boolean>>(new Map());

    /** Whether the search field is shown. */
    readonly searchVisible = input(true);

    /** Horizontal alignment class for the search row. */
    readonly searchPosition = input(HorizontalPosition.START);

    /** Languages expected for complete FAQ translations (admin completeness check). */
    readonly supportedLanguages = input<LanguageCode[]>([]);

    /** Whether an FAQ order update is in progress. */
    readonly orderUpdateInProgress = input(false);

    readonly onDelete = output<FaqListModel>();
    readonly onEdit = output<FaqListModel>();
    readonly onOrderChange = output<FaqListModel[]>();

    protected readonly HorizontalPosition = HorizontalPosition;

    protected get defaultLang(): LanguageCode {
        return this.localizationService.defaultLanguage;
    }

    protected currLang: WritableSignal<string> = signal(
        this.localizationService.getCurrentLanguage()
    );
    protected expandedFaqIds: WritableSignal<Set<number>> = signal(new Set());
    protected faqSearch: FormControl<string> = new FormControl<string>(
        ''
    ) as FormControl;

    private debouncedFilter = signal('');

    private readonly searchableFaqs = computed(() =>
        this.faqs().map((faq) => ({
            faq,
            question: LocalizationService.removeDiacritics(
                faq.question.translations[this.currLang()] ||
                    faq.question.translations[this.defaultLang] ||
                    ''
            ).toLowerCase(),
            answer: LocalizationService.removeDiacritics(
                stripHtmlTags(
                    faq.answer.translations[this.currLang()] ||
                        faq.answer.translations[this.defaultLang] ||
                        ''
                )
            ).toLowerCase(),
        }))
    );

    protected filteredFaqs = computed(() =>
        this.filterFaqs(this.debouncedFilter())
    );

    constructor(
        private readonly destroyRef: DestroyRef,
        private readonly localizationService: LocalizationService,
        private readonly translateService: TranslateService
    ) {
        this.listenForSearchChanges();
    }

    /**
     * Listens for changes in the search input and language changes to trigger filtering of FAQs.
     *
     * @listens faqSearch.valueChanges Observable for changes in the search input.
     * @listens translateService.onLangChange Observable for language changes.
     *
     * @description This function sets up observables to monitor changes in both the search input
     * and language changes using valueChanges and onLangChange. It then merges these
     * observables and applies debouncing and distinctUntilChanged to ensure smooth filtering.
     * Debounced text updates `debouncedFilter`; language updates `currLang`. The filtered list
     * is derived by `filteredFaqs` computed from those signals and `faqs`.
     */
    private listenForSearchChanges(): void {
        const filterChanges$ = this.faqSearch.valueChanges;
        const languageChanges$ = this.translateService.onLangChange;
        const merged$ = merge(filterChanges$, languageChanges$);

        merged$
            .pipe(
                startWith(''),
                debounceTime(ConstantsService.FILTER_DEBOUNCE_TIME),
                distinctUntilChanged(),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((result) => {
                if (typeof result === 'string') {
                    this.debouncedFilter.set(result);
                } else {
                    this.currLang.set(result.lang);
                }
            });
    }

    private filterFaqs(filter: string): FaqListModel[] {
        const normalizedFilter = LocalizationService.removeDiacritics(
            filter.toLowerCase()
        );

        return this.searchableFaqs()
            .filter(
                ({ question, answer }) =>
                    question.includes(normalizedFilter) ||
                    answer.includes(normalizedFilter)
            )
            .map(({ faq }) => faq);
    }

    protected deleteFaq(faq: FaqListModel): void {
        this.onDelete.emit(faq);
    }

    protected editFaq(faq: FaqListModel): void {
        this.onEdit.emit(faq);
    }

    protected moveFaqUp(faq: FaqListModel): void {
        const faqs = [...this.faqs()];
        const index = faqs.findIndex((item) => item.id === faq.id);

        if (index <= 0) {
            return;
        }

        moveItemInArray(faqs, index, index - 1);
        this.emitOrderChange(faqs);
    }

    protected moveFaqDown(faq: FaqListModel): void {
        const faqs = [...this.faqs()];
        const index = faqs.findIndex((item) => item.id === faq.id);

        if (index < 0 || index >= faqs.length - 1) {
            return;
        }

        moveItemInArray(faqs, index, index + 1);
        this.emitOrderChange(faqs);
    }

    protected dropFaq(event: CdkDragDrop<FaqListModel[]>): void {
        if (event.previousIndex === event.currentIndex) {
            return;
        }

        const faqs = [...this.faqs()];
        moveItemInArray(faqs, event.previousIndex, event.currentIndex);

        this.emitOrderChange(faqs);
    }

    private emitOrderChange(faqs: FaqListModel[]): void {
        this.onOrderChange.emit(faqs);
    }

    protected openFaq(faqId: number): void {
        this.expandedFaqIds.update((ids) => {
            const next = new Set(ids);
            next.add(faqId);
            return next;
        });
    }

    protected closeFaq(faqId: number): void {
        this.expandedFaqIds.update((ids) => {
            const next = new Set(ids);
            next.delete(faqId);
            return next;
        });
    }

    protected getReorderTooltip(
        defaultTooltip?: 'shared.faq.moveUp' | 'shared.faq.moveDown'
    ): string {
        if (this.orderUpdateInProgress()) {
            return this.translateService.instant(
                'shared.faq.reorderingInProgress'
            );
        }

        if (this.faqSearch.value) {
            return this.translateService.instant(
                'shared.faq.clearSearchToReorder'
            );
        }

        return defaultTooltip
            ? this.translateService.instant(defaultTooltip)
            : '';
    }

    /**
     * Checks if a FAQ item has translations for all supported languages.
     *
     * @param faq The FAQ item to be checked.
     * @returns True if the FAQ has translations for all supported languages, false otherwise.
     */
    protected hasAllTranslations(faq: FaqListModel): boolean {
        const faqQuestionLanguages = Object.values(faq.question.translations);
        const faqAnswerLanguages = Object.values(faq.answer.translations);
        const numOfSupportedLanguages = this.supportedLanguages().length;

        const undefinedLanguage =
            faqQuestionLanguages.length < numOfSupportedLanguages ||
            faqAnswerLanguages.length < numOfSupportedLanguages;

        const emptyString =
            faqQuestionLanguages.some((val) => val === '') ||
            faqAnswerLanguages.some((val) => val === '');

        return !(undefinedLanguage || emptyString);
    }

    protected resetFilter(): void {
        this.debouncedFilter.set('');
        this.faqSearch.setValue('', { emitEvent: false });
    }
}
